import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Download as GetAppIcon,
  Mail as EmailIcon,
  Printer as FaxIcon,
  CheckCircle2 as CheckCircleIcon,
  FileType as PictureAsPdfIcon,
  Send as SendIcon,
  CheckCheck as DoneIcon,
  History as HistoryIcon
} from 'lucide-react';

import api from '../../../utils/axios';

const ExportStep = ({ appeal, onBack, onUpdate, onError, onSuccess, onComplete }) => {
  const [exporting, setExporting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfSize, setPdfSize] = useState(null);
  const [sendMethod, setSendMethod] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientFax, setRecipientFax] = useState('');
  const [sending, setSending] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [appealStatus, setAppealStatus] = useState('draft');

  useEffect(() => {
    if (appeal) {
      setAppealStatus(appeal.status || 'draft');

      // Check if PDF already exists
      if (appeal.letter && appeal.letter.rendered_pdf_path) {
        setPdfUrl(`/documents/minimal-appeals/${appeal.id}/download/`);
        // Estimate file size or get from server
        setPdfSize('~150 KB');
      }
    }
  }, [appeal]);

  const exportToPdf = async () => {
    if (!appeal || !appeal.id || !appeal.letter) {
      onError('No letter content found to export');
      return;
    }

    setExporting(true);
    onError(null);

    try {
      const response = await api.post(`/documents/minimal-appeals/${appeal.id}/export/`);

      setPdfUrl(response.data.pdf_url);
      setPdfSize(`~${Math.round(response.data.size_bytes / 1024)} KB`);

      onSuccess('PDF exported successfully! You can now download or send it.');

      // Update the appeal data
      const updatedAppeal = await api.get(`/documents/minimal-appeals/${appeal.id}/`);
      onUpdate(updatedAppeal.data);

    } catch (err) {
      onError(err.response?.data?.error || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const downloadPdf = () => {
    if (!appeal?.id) {
      onError('Appeal not found');
      return;
    }

    api.get(`/documents/minimal-appeals/${appeal.id}/download/`, {
      responseType: 'blob',
    }).then((response) => {
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch?.[1] || `appeal-${appeal?.patient_name || 'letter'}-${new Date().toISOString().split('T')[0]}.pdf`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      onSuccess('PDF downloaded successfully!');
    }).catch(async (err) => {
      const apiError = err.response?.data;
      if (apiError instanceof Blob) {
        try {
          const text = await apiError.text();
          const parsed = JSON.parse(text);
          onError(parsed?.error || 'Failed to download PDF');
          return;
        } catch {
          onError('Failed to download PDF');
          return;
        }
      }
      onError(err.response?.data?.error || 'Failed to download PDF');
    });
  };

  const handleSend = async () => {
    if (!appeal || !appeal.id) {
      onError('Appeal not found');
      return;
    }

    if (!sendMethod || (!recipientEmail && sendMethod === 'email') || (!recipientFax && sendMethod === 'fax')) {
      onError('Please provide recipient information');
      return;
    }

    setSending(true);
    onError(null);

    try {
      const sendData = {
        method: sendMethod,
        to: sendMethod === 'email' ? recipientEmail : recipientFax
      };

      await api.post(`/documents/minimal-appeals/${appeal.id}/send/`, sendData);

      // Update appeal status to sent
      const updateResponse = await api.patch(`/documents/minimal-appeals/${appeal.id}/`, {
        status: 'sent'
      });

      setAppealStatus('sent');
      onUpdate(updateResponse.data);
      setSendDialogOpen(false);
      setCompletionDialogOpen(true);

      onSuccess(`Appeal sent successfully via ${sendMethod}!`);

    } catch (err) {
      onError(err.response?.data?.error || `Failed to send via ${sendMethod}`);
    } finally {
      setSending(false);
    }
  };

  const markAsSent = async () => {
    if (!appeal || !appeal.id) {
      onError('Appeal not found');
      return;
    }

    try {
      const response = await api.patch(`/documents/minimal-appeals/${appeal.id}/`, {
        status: 'sent'
      });

      setAppealStatus('sent');
      onUpdate(response.data);
      setCompletionDialogOpen(true);

      onSuccess('Appeal marked as sent!');

    } catch (err) {
      onError('Failed to update appeal status');
    }
  };

  const renderExportInterface = () => {
    if (!pdfUrl) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PictureAsPdfIcon size={60} />
            <Typography variant="h6" gutterBottom>
              Export to PDF
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Generate a professional PDF version of your appeal letter ready for submission.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={exporting ? <CircularProgress size={20} /> : <PictureAsPdfIcon size={16} />}
              onClick={exportToPdf}
              disabled={exporting}
            >
              {exporting ? 'Generating PDF...' : 'Generate PDF'}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircleIcon size={16} color="var(--ab-approved)" />
            <Typography variant="h6" color="success.main">
              PDF Ready for Download
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mb={3}>
            <PictureAsPdfIcon size={16} />
            <Typography variant="body1">
              appeal-letter.pdf ({pdfSize})
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<GetAppIcon size={16} />}
                onClick={downloadPdf}
                size="large"
              >
                Download PDF
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EmailIcon size={16} />}
                onClick={() => {
                  setSendMethod('email');
                  setSendDialogOpen(true);
                }}
                size="large"
              >
                Send via Email
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FaxIcon size={16} />}
                onClick={() => {
                  setSendMethod('fax');
                  setSendDialogOpen(true);
                }}
                size="large"
              >
                Send via Fax
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderAppealSummary = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Appeal Summary
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Patient Information"
              secondary={`${appeal?.patient_name || 'N/A'} - ${appeal?.claim_number || 'No claim #'}`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Insurance Payer"
              secondary={appeal?.payer_name || 'Not specified'}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Documents Uploaded"
              secondary={`${appeal?.documents?.length || 0} files processed`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Letter Status"
              secondary="Generated and ready for export"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">
            Current Status:
          </Typography>
          <Chip
            label={appealStatus === 'sent' ? 'Sent' : 'Draft'}
            color={appealStatus === 'sent' ? 'success' : 'default'}
            icon={appealStatus === 'sent' ? <DoneIcon size={16} /> : <HistoryIcon size={16} />}
          />
        </Box>
      </CardContent>
    </Card>
  );

  // Show loading state if appeal is not loaded yet
  if (!appeal || !appeal.id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading appeal data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Step 5: Export & Send
      </Typography>

      <Typography variant="body1" color="textSecondary" paragraph>
        Export your appeal letter to PDF and send it to the insurance company.
      </Typography>

      {renderAppealSummary()}
      {renderExportInterface()}

      {/* Manual Status Update */}
      {pdfUrl && appealStatus === 'draft' && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Manual Submission
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              If you prefer to submit the appeal manually or through your own channels,
              you can mark it as sent to complete the process.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DoneIcon size={16} />}
              onClick={markAsSent}
            >
              Mark as Sent
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Appeal Letter {sendMethod === 'email' ? 'via Email' : 'via Fax'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {sendMethod === 'email' ? (
              <TextField
                fullWidth
                label="Recipient Email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="appeals@insurance.com"
                helperText="Enter the insurance company's appeals email address"
              />
            ) : (
              <TextField
                fullWidth
                label="Recipient Fax Number"
                value={recipientFax}
                onChange={(e) => setRecipientFax(e.target.value)}
                placeholder="+1-800-555-0199"
                helperText="Enter the insurance company's appeals fax number"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon size={16} />}
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Appeal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog open={completionDialogOpen} onClose={() => setCompletionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <CheckCircleIcon size={60} color="var(--ab-approved)" />
          <Typography variant="h5">Appeal Completed!</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" paragraph>
            Your appeal letter has been successfully generated and sent.
            You can track the status and view your appeals in the dashboard.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Keep a copy of your appeal for your records and follow up with the insurance company
            if you don't receive a response within their specified timeframe.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="outlined" onClick={() => setCompletionDialogOpen(false)}>
            Close
          </Button>
          <Button variant="contained" onClick={onComplete}>
            View All Appeals
          </Button>
        </DialogActions>
      </Dialog>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
        >
          Back: Review Letter
        </Button>

        {appealStatus === 'sent' ? (
          <Button
            variant="contained"
            onClick={onComplete}
            size="large"
            startIcon={<CheckCircleIcon size={16} />}
          >
            Complete & View Appeals
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={onComplete}
            size="large"
          >
            Save as Draft & Exit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ExportStep;
